/* ============================================================================
   bsfn-label-callout.js — reveals the label callout when it scrolls into view
   and counts the called-out number down to its declared value.

   ORIGIN  ORIGINAL — authored for BSFN. Uses IntersectionObserver
           (developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver) and
           no library. The reveal itself is CSS: this file only adds and
           removes one `is-in` class.

   PROGRESSIVE ENHANCEMENT, ON PURPOSE
   The server renders every callout with `is-in` ALREADY ON — fully drawn. This
   script's first act is to take it off, and only for elements that opted into
   motion. So a blocked script, a parse error, an old browser or
   prefers-reduced-motion all leave the element finished and readable rather
   than blank. Nothing here is load-bearing for the content.

   THE NUMBER  The declared value is authored as text ("0g", "0 g", "<1g").
   Counting parses the FIRST number out of it, animates from `data-lc-from`,
   and on the last frame writes the authored string back verbatim — an FDA
   panel figure must never be left as something this script rounded to. The
   animated element is aria-hidden with the declared value beside it in a
   visually hidden span, so a screen reader reads the figure once and never
   mid-count.
   ========================================================================== */
(function () {
  if (window.BSFN && window.BSFN.labelCallout) return;
  var BSFN = (window.BSFN = window.BSFN || {});

  var REDUCED = false;
  try { REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var DURATION = 900;   // count-up, ms
  var DELAY = 620;      // starts after the card has arrived, ms

  /* Pull the first number out of an authored value, keeping what sits around
     it: "0 g" -> {pre:'', num:0, post:' g'}; "<1g" -> {pre:'<', num:1, ...}. */
  function parseValue(text) {
    var m = /-?\d+(?:\.\d+)?/.exec(text || '');
    if (!m) return null;
    return {
      pre: text.slice(0, m.index),
      num: parseFloat(m[0]),
      post: text.slice(m.index + m[0].length),
      decimals: (m[0].split('.')[1] || '').length
    };
  }

  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

  /* Resolve an element into a countable plan, or null when there is nothing to
     count (no from, not a number, or it already equals the declared value). */
  function plan(el) {
    if (!el) return null;
    var target = el.getAttribute('data-lc-to');
    var from = parseFloat(el.getAttribute('data-lc-from'));
    var to = parseValue(target);
    if (!to || isNaN(from) || from === to.num) return null;
    return { el: el, target: target, from: from, to: to };
  }

  function write(p, n) { p.el.textContent = p.to.pre + n.toFixed(p.to.decimals) + p.to.post; }

  function count(p) {
    var started = 0;
    function frame(now) {
      if (!started) started = now;
      var t = Math.min(1, (now - started) / DURATION);
      if (t >= 1) { p.el.textContent = p.target; return; }   // exact declared value
      write(p, p.from + (p.to.num - p.from) * easeOut(t));
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function reveal(el) {
    /* Put the start value up BEFORE the card fades in, not when the count
       begins: otherwise the card arrives reading the declared value and then
       jumps back up to the start, which reads as a glitch. */
    var p = plan(el.querySelector('[data-lc-to]'));
    if (p) write(p, p.from);
    el.classList.add('is-in');
    if (p) setTimeout(function () { count(p); }, DELAY);
  }

  function arm(el) {
    if (el.__bsfnLC) return;
    el.__bsfnLC = true;
    if (REDUCED || el.getAttribute('data-bsfn-lc') !== 'animate') return; // stays drawn

    el.classList.remove('is-in');

    if (!('IntersectionObserver' in window) || !window.requestAnimationFrame) {
      reveal(el);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        reveal(entry.target);
      });
    }, { threshold: 0.25, rootMargin: '0px 0px -5% 0px' });
    io.observe(el);

    /* Safety net, same idea as bsfn.js: an element that never trips the
       observer (a hidden tab, a container that never scrolls) still finishes. */
    setTimeout(function () { if (!el.classList.contains('is-in')) { io.unobserve(el); reveal(el); } }, 4000);
  }

  function init(root) {
    try {
      var els = (root || document).querySelectorAll('[data-bsfn-lc]');
      Array.prototype.forEach.call(els, arm);
    } catch (err) {
      /* Never leave a callout half-drawn because of a script failure. */
      try {
        Array.prototype.forEach.call(document.querySelectorAll('[data-bsfn-lc]'), function (el) {
          el.classList.add('is-in');
        });
      } catch (e) {}
    }
  }

  BSFN.labelCallout = { init: init };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* The theme editor re-renders a section when its settings change. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
